import {
  AfterViewInit,
  ChangeDetectorRef,
  ElementRef,
  Injectable,
  OnInit,
  TemplateRef,
  ViewChild,
  Directive,
  HostListener
} from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import {
  EpApplicationService,
  EpButtonSize,
  EpButtonStyle,
  EpButtonType,
  EpLocalStorageService,
  EpShellService,
  EpToolBarComponent,
  EpToolBarType,
  IEpButtonModel,
  IEpChipSelectModel,
  IEpComboBoxModel,
  IEpSelectionListMode,
  IEpSelectionListModel,
  IEpSlidingPanelActivityData,
  IEpSlidingPanelResult,
  IEpTabStripModel,
  IEpToolBarModel
} from '@epicor/ux-platform';
import { Spec, SwaggerConfigs, SwaggerRequest, SwaggerUIBundle } from 'swagger-ui-dist';
import { SwaggerDocService } from '../services/swagger-doc.service';
import { ISetHeaderPanelModel, SetHeaderComponent } from '../set-header/set-header.component';
import { HeaderService } from '../services/header.service';
import {
  AuthorizationHeaderProcessing,
  CustomHeader,
  HeaderTypes,
  IAuthorizationHeaderSettings,
  ICallHeader
} from '../models/headers';
import { RestVersions } from '../models/rest-versions';
import { HelpType } from '../models/help-type';
import { HelpPageService } from '../services/help-page.service';
import { AppLocalizationStrings } from '../app.localization.strings';
import { restHelpCurCompStorageKey, restHelpVersionStorageKey } from '../app.constants';
import { HelpPageType, HelpPageTypes } from '../models/help-page-types';
import { Observable, of } from 'rxjs';
import { concatMap } from 'rxjs/operators';

export interface IMethodsList extends IEpSelectionListModel {
  allItems: any[];
}

export enum DefaultParameters {
  XApiKey = 'X-API-Key',
  CurrentCompany = 'currentCompany'
}

export enum MethodTabs {
  OData = 'OData',
  RPC = 'RPC',
  BAQ = 'BAQ',
  Efx = 'Function'
}

@Directive()
export abstract class SelectHelpComponent implements OnInit, AfterViewInit {
  @ViewChild(EpToolBarComponent) toolbar: EpToolBarComponent;
  @ViewChild('toolbarTemplate', { static: true }) toolbarTemplate: TemplateRef<any>;
  @ViewChild('tabTemplate', { static: true }) tabTemplate: TemplateRef<any>;
  @ViewChild('list') elementList: ElementRef;
  @ViewChild('helpui', { static: true }) helpUI: ElementRef;

  appStrings = AppLocalizationStrings;

  setHeaderPanelModel: ISetHeaderPanelModel = {
    headerType: HeaderTypes.ApiKey,
    headerValue: null,
    canEditName: false,
    onOkHandler: () => {},
    onCancelHandler: () => {}
  };

  EpToolBarType = EpToolBarType;
  panelListModel: IMethodsList;
  EpButtonStyle = EpButtonStyle;

  companyId: string;
  currentSearch: string;

  public headerComboBox: IEpComboBoxModel = {
    labelText: this.appStrings.SelectHeaderLbl,
    name: 'headers',
    data: [],
    epOnOpen: item => this.setComboHeaderList(item.model)
  };

  addHeaderButton: IEpButtonModel = {
    labelText: this.appStrings.lblAddHeader,
    size: EpButtonSize.Medium,
    type: EpButtonType.Button,
    style: EpButtonStyle.Outline,
    epOnClick: item => this.addHeader(item)
  };
  version = RestVersions.V2; // v1 and v2

  toolBarModel: IEpToolBarModel = {
    items: [
      {
        id: '1',
        order: 1,
        type: EpToolBarType.Template
      },
      {
        id: 'sep',
        order: 2,
        type: EpToolBarType.Separator
      },
      {
        id: '2',
        order: 3,
        type: EpToolBarType.ComboBox,
        itemModel: this.headerComboBox
      },
      {
        id: '3',
        order: 4,
        type: EpToolBarType.Button,
        itemModel: this.addHeaderButton
      }
    ]
  };

  public allHeaders: Array<HeaderTypes> = Object.values(HeaderTypes);
  public selectedHeaders: Array<HeaderTypes> = [HeaderTypes.ApiKey];

  apiKeyPlaceHolder = this.appStrings.apiKeyPlaceHolder;

  headersChips: IEpChipSelectModel = {
    labelText: 'Headers',
    data: [...this.allHeaders],
    epOnRemoveChip: item => {
      this.onRemoveChip(item);
    },
    epOnChipClick: item => {
      this.onClickOnChip(item);
    }
  };

  tabModel: IEpTabStripModel = {
    data: [],
    tabPosition: 'top'
  };

  helpTitle: string;
  versions: string[] = [RestVersions.V1, RestVersions.V2];
  companies: string[];

  protected constructor(
    private route: ActivatedRoute,
    protected router: Router,
    protected epApplicationService: EpApplicationService,
    protected swaggerDocService: SwaggerDocService,
    private cdr: ChangeDetectorRef,
    protected epShellService: EpShellService,
    private headerService: HeaderService,
    protected helpPageService: HelpPageService,
    private epLocalStorageService: EpLocalStorageService
  ) {}

  ngOnInit() {
    this.tabModel.data = [
      {
        title: MethodTabs.OData,
        template: this.tabTemplate,
        hidden: this.isTabHidden(HelpType.OData)
      },
      {
        title: MethodTabs.RPC,
        template: this.tabTemplate,
        hidden: this.isTabHidden(HelpType.Methods)
      },
      {
        title: MethodTabs.BAQ,
        template: this.tabTemplate,
        hidden: this.isTabHidden(HelpType.Baq)
      },
      {
        title: MethodTabs.Efx,
        template: this.tabTemplate,
        hidden: this.isTabHidden(HelpType.Efx)
      }
    ];

    this.panelListModel = {
      items: [],
      mode: IEpSelectionListMode.Panel,
      allItems: [] // to store unfiltered
    };
    this.currentSearch = '';

    this.helpPageService.getCompanies().subscribe({
      next: list => {
        this.companies = list;
        if (!this.companyId) {
          this.companyId = this.companies[0];
        }
      }
    });
  }

  ngAfterViewInit(): void {
    this.toolBarModel.items[0].template = this.toolbarTemplate;
    this.cdr.detectChanges();

    this.route.params.subscribe(routeParams => {
      this.loadItems(routeParams);
    });

    this.setHeights(window.innerHeight);
  }

  // former host:'(window:resize)': 'onResize($event)'
  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.setHeights(event.target.innerHeight);
  }

  setHeights(windowsHeight) {
    if (!this.elementList) {
      return;
    }
    const pad = 35;
    const listTop = this.elementList.nativeElement.getBoundingClientRect().top;
    this.elementList.nativeElement.style = 'height:' + (windowsHeight - listTop - pad) + 'px';
    const helpDivTop = this.helpUI.nativeElement.getBoundingClientRect().top;
    this.helpUI.nativeElement.style = 'height: ' + (windowsHeight - helpDivTop - pad) + 'px';
  }

  abstract processRouteParams(params): void;

  abstract selectHelpForType(): void;

  abstract isTabHidden(panelHelpType: HelpType): boolean;

  abstract onTabSelect(item): void;

  abstract showHelpUI(item): void;

  abstract searchMethod(item): void;

  abstract shouldShowSelectItemMessage(): boolean;

  abstract selectItemMessage(): string;

  abstract changeCompany(item): void;

  abstract changeVersion(item): void;

  abstract versionComboHidden(): boolean;

  abstract companyComboHidden(): boolean;

  abstract getLegacyHelpPageLink(): string;

  abstract shouldShowLinks(): boolean;

  abstract getSwaggerDocLink(): string;

  loadItems(params: Params) {
    this.companyId = this.epLocalStorageService.get(restHelpCurCompStorageKey);

    let version = params['version'];

    if (!version) {
      version = RestVersions.V2;
    }

    this.version = version;

    this.saveVersion();

    this.processRouteParams(params);

    this.selectHelpForType();

    this.cdr.detectChanges();
  }

  saveVersion() {
    // save last used version
    this.epLocalStorageService.set(restHelpVersionStorageKey, this.version);
  }

  shouldUseExtensions(): Observable<boolean> {
    return this.helpPageService.getHelpPageType().pipe(
      concatMap(h => {
        return of(h.helpPageType === HelpPageTypes.OpenAPI);
      })
    );
  }

  getOpenApiDocLinkWithExt(ext): string {
    return this.getSwaggerDocLink() + '.' + ext;
  }

  getPartialDoc(spec: Spec, methodInSpec: object): Spec {
    if (spec.swagger) {
      // v2
      return this.createSwaggerPartialDoc(spec, methodInSpec);
    } else {
      // v3
      return this.createOpenApiPartialDoc(spec, methodInSpec);
    }
  }

  /***
   * change values from this spec. do not use on cached.
   * @param spec - swagger doc
   */
  updateCurrentSpec(spec: Spec): Spec {
    if (spec.swagger) {
      return this.updateSwaggerCurrentSpec(spec);
    } else {
      return this.updateOpenApiCurrentSpec(spec);
    }
  }

  /***
   * Create new spec from existing. it is shallow copy though, so be careful.
   * @param spec - swagger doc
   */

  private createSwaggerPartialDoc(spec: Spec, methodInSpec: object) {
    const currentSpec = this.createSwaggerBaseSpec(spec);
    currentSpec.paths = methodInSpec;
    this.getSwaggerRelatedDefinitions(methodInSpec, spec, currentSpec);
    return currentSpec;
  }

  /***
   * Create new spec from existing. it is shallow copy though, so be careful.
   * @param spec - swagger doc
   */

  private createOpenApiPartialDoc(spec: Spec, methodInSpec: object) {
    const currentSpec = this.createOpenApiBaseSpec(spec);
    currentSpec.paths = methodInSpec;
    this.getOpenApiRelatedComponents(methodInSpec, spec, currentSpec);
    return currentSpec;
  }

  private createSwaggerBaseSpec(spec: Spec) {
    const currentSpec = <Spec>{
      swagger: spec.swagger,
      host: spec.host,
      basePath: spec.basePath,
      consumes: spec.consumes,
      schemes: spec.schemes,
      info: spec.info
    };

    currentSpec.definitions = {};
    currentSpec.parameters = spec.parameters;
    this.setParameterValues(currentSpec.parameters);

    return currentSpec;
  }

  private updateSwaggerCurrentSpec(spec: Spec) {
    this.setParameterValues(spec.parameters);
    return spec;
  }

  private createOpenApiBaseSpec(spec: Spec) {
    const currentSpec = <Spec>{
      openapi: spec.openapi,
      servers: spec.servers,
      info: spec.info,
      tags: spec.tags
    };

    currentSpec.components = {};
    currentSpec.components.schemas = {};
    currentSpec.components.parameters = spec.components.parameters;
    this.setParameterValues(currentSpec.components.parameters);

    return currentSpec;
  }

  private updateOpenApiCurrentSpec(spec: Spec) {
    this.setParameterValues(spec.components.parameters);
    return spec;
  }

  private getSwaggerRelatedDefinitions(methodInSpec: object, fullSpec: Spec, currentSpec: Spec) {
    const schemasToAdd = this.getProps(methodInSpec);

    const processLevel = (refToAdd: []) => {
      refToAdd.forEach(currentRef => {
        const ref = currentRef as any;
        const ind = ref.lastIndexOf('/');
        const defName = ref.substring(ind + 1);
        const childDef = fullSpec.definitions[defName];
        if (childDef && !currentSpec.definitions.hasOwnProperty(defName)) {
          currentSpec.definitions[defName] = childDef;
          const childRefToAdd = this.getProps(childDef);
          processLevel(childRefToAdd);
        }
      });
    };
    processLevel(schemasToAdd);
  }

  private getOpenApiRelatedComponents(methodInSpec: object, fullSpec: Spec, currentSpec: Spec) {
    const schemasToAdd = this.getProps(methodInSpec);

    const processLevel = (refToAdd: []) => {
      refToAdd.forEach(currentRef => {
        const ref = currentRef as any;
        const ind = ref.lastIndexOf('/');
        const defName = ref.substring(ind + 1);

        // check schemas and copy those that are used in the current call
        if (fullSpec.components.schemas) {
          const childDef = fullSpec.components.schemas[defName];
          if (childDef && !currentSpec.components.schemas.hasOwnProperty(defName)) {
            currentSpec.components.schemas[defName] = childDef;
            const childRefToAdd = this.getProps(childDef);
            processLevel(childRefToAdd);
          }
        }

        // check parameters
        if (fullSpec.components.parameters) {
          const childDef = fullSpec.components.parameters[defName];
          if (childDef && !currentSpec.components.parameters.hasOwnProperty(defName)) {
            currentSpec.components.parameters[defName] = childDef;
            const childRefToAdd = this.getProps(childDef);
            processLevel(childRefToAdd);
          }
        }
      });
    };
    processLevel(schemasToAdd);
  }

  getProps(myObj): [] {
    function findAllByKey(obj, keyToFind) {
      return (
        Object.entries(obj).reduce(
          (acc, [key, value]) =>
            key === keyToFind
              ? acc.concat(value)
              : typeof value === 'object' && value
              ? acc.concat(findAllByKey(value, keyToFind))
              : acc,
          []
        ) || []
      );
    }

    return findAllByKey(myObj, '$ref');
  }

  private setParameterValues(parameters) {
    if (!parameters) {
      return;
    }

    if (parameters.hasOwnProperty(DefaultParameters.XApiKey)) {
      const apiKey = this.headerService.getHeader(HeaderTypes.ApiKey);
      if (apiKey.Value !== '') {
        parameters[DefaultParameters.XApiKey].value = apiKey.Value;
      } else {
        parameters[DefaultParameters.XApiKey].value = this.apiKeyPlaceHolder;
      }
      // parameters.required = false;
    }
    if (parameters.hasOwnProperty(DefaultParameters.CurrentCompany)) {
      parameters[DefaultParameters.CurrentCompany].value = this.companyId;
    }
  }

  showSwaggerUI(options: { currentSpec?: Spec; swaggerUrl?: string }) {
    const swaggerOptions: SwaggerConfigs = {
      dom_id: '#swagger-ui',
      layout: 'BaseLayout',
      presets: [SwaggerUIBundle.presets.apis, SwaggerUIBundle.SwaggerUIStandalonePreset],

      docExpansion: 'list',
      //   operationsSorter: 'alpha',
      plugins: [SwaggerUIBundle.plugins.DownloadUrl],
      deepLinking: false,
      defaultModelsExpandDepth: 1,
      defaultModelExpandDepth: 1,
      defaultModelRendering: 'example',
      displayRequestDuration: true,

      requestInterceptor: req => {
        this.setHeadersOnCall(req);

        return req;
      },

      responseInterceptor: resp => {
        //  console.log(resp);
        return resp;
      }
    };

    if (options.currentSpec) {
      swaggerOptions.spec = options.currentSpec;
    } else if (options.swaggerUrl) {
      swaggerOptions.url = options.swaggerUrl;
    }

    const ui = SwaggerUIBundle(swaggerOptions);
  }

  private getCurrentHeader(headerType: HeaderTypes): ICallHeader {
    return this.headerService.getHeader(headerType);
  }

  private setHeadersOnCall(req: SwaggerRequest) {
    let authorizationHeaderProcessed = false;
    this.selectedHeaders.forEach(hdr => {
      const cachedHeader = this.headerService.getHeader(hdr);

      switch (hdr) {
        case HeaderTypes.ApiKey: {
          if (cachedHeader.Value !== '') {
            req.headers[cachedHeader.Name] = cachedHeader.Value;
          }
          break;
        }
        case HeaderTypes.CallSettings:
        case HeaderTypes.License:
        case HeaderTypes.SessionInfo:
        case HeaderTypes.CallInfo: {
          if (cachedHeader.Value !== null) {
            req.headers[cachedHeader.Name] = JSON.stringify(cachedHeader.Value);
          }
          break;
        }
        case HeaderTypes.Authorization:
          if (cachedHeader.Value) {
            authorizationHeaderProcessed = this.processAuthorizationHeader(req, cachedHeader.Value);
          }
          break;

        default: {
          if (cachedHeader.Value) {
            req.headers[cachedHeader.Name] = cachedHeader.Value;
          }
          break;
        }
      }
    });
    // set default authorization header
    if (!authorizationHeaderProcessed) {
      const token = this.epApplicationService.tokenService.token;
      req.headers.Authorization = 'Bearer ' + token;
    }
  }

  processAuthorizationHeader(req: SwaggerRequest, header: IAuthorizationHeaderSettings): boolean {
    switch (header.ProcessingType) {
      case AuthorizationHeaderProcessing.SkipHeader:
        return true;
      case AuthorizationHeaderProcessing.UseHeaderValue: {
        req.headers.Authorization = header.Value;
        return true;
      }
      case AuthorizationHeaderProcessing.UseBasicAuth:
        {
          const basicAuth = 'Basic ' + btoa(header.UserName + ':' + header.Password);
          req.headers.Authorization = basicAuth;
          return true;
        }
        return false;
    }
  }

  private addHeader(item) {
    const newHeader = this.headerComboBox.value;

    if (!newHeader || this.selectedHeaders.includes(newHeader)) {
      return;
    }

    this.showHeaderPanel(newHeader);
  }

  private setComboHeaderList(model) {
    model.data = this.allHeaders.filter(h => {
      return !this.selectedHeaders.includes(h);
    });
  }

  onRemoveChip($event: any) {}

  onClickOnChip($event: any) {
    // Only way I found to get text of the chip clicked on.
    // noinspection JSDeprecatedSymbols
    const text = $event.event.event.srcElement.innerText;
    this.showHeaderPanel(<HeaderTypes>text);
  }

  private showHeaderPanel(headerType: HeaderTypes) {
    this.showHeader(headerType);

    const options: IEpSlidingPanelActivityData = {
      model: this.setHeaderPanelModel,
      component: SetHeaderComponent,
      id: 'SetHeaderComponent',
      returnHandler: result => this.processSetHeaderResult(result)
    };

    this.epShellService.showSlidePanelActivity(options);
  }

  private showHeader(headerType: HeaderTypes) {
    let headerValue = this.getCurrentHeader(headerType);

    this.setHeaderPanelModel.canEditName = false;

    if (headerValue == null) {
      headerValue = new CustomHeader();
      this.setHeaderPanelModel.canEditName = true;
    } else {
      headerValue = { ...headerValue };
    }

    this.setHeaderPanelModel.headerType = headerType;
    this.setHeaderPanelModel.headerValue = headerValue;
  }

  private processSetHeaderResult(result: IEpSlidingPanelActivityData) {
    if (result.action === IEpSlidingPanelResult.Cancel) {
      return true;
    }

    this.headerComboBox.value = undefined;

    const type = this.setHeaderPanelModel.headerType;

    this.headerService.setHeader(type, this.setHeaderPanelModel.headerValue);

    if (!this.selectedHeaders.includes(type)) {
      this.selectedHeaders.push(type);
    }
    return true;
  }
}
